import React, { useEffect, useMemo, useState } from 'react'
import { slugs } from '@/constants/slugs'
import { getWebClient } from '@/services/ApolloClient'
import { getWatchCollectionContent } from '@/services/CollectionsService'
import { WatchCollectionEdge, WatchCollectionItem } from '@/services/CollectionsService/types'
import { useGuildMember } from '@/services/GuildUserService'
import { Project } from '@/services/ProjectsService'
import {
  isWatchCollectionEpisode,
  isWatchCollectionMovie,
  mapWatchCollectionEpisodeNodeToMedia,
  mapWatchCollectionMovieNodeToMedia,
} from '@/utils/CollectionContentUtil'
import { Media, isASneakPeek } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { EpisodeCard } from '@/views/WatchProjectTabsView/WatchEpisodesTab/EpisodeCard'
import { ManualUpcomingCollectionContent, WATCH_COLLECTION_UPCOMING_CONTENT } from './upcomingEpisodes'

interface WatchCollectionTabProps {
  collectionId: string
  projectData: Project
  slug: string
  isGeoblockedProject: boolean
  hasWatchableReasons: boolean
}

interface TransformNodeOptions {
  slug: string
  projectType?: string
  isGeoblockedProject: boolean
  isGuildMember: boolean
}

const getManualAvailabilityDate = (entry: ManualUpcomingCollectionContent): string | undefined =>
  entry.publiclyAvailableDate ?? entry.earlyAccessDate ?? entry.guildAvailableDate ?? entry.releaseDate ?? undefined

const UPCOMING_CONTENT_SLUG_ALIASES: Record<string, string> = {
  [slugs.homestead]: slugs.homesteadSeries,
}

const mapManualContentToMedia = (
  entry: ManualUpcomingCollectionContent,
  projectType: string | undefined,
  currentSlug: string,
): Media | null => {
  const poster = entry.posterLandscapeCloudinaryPath ?? undefined
  const availabilityDate = getManualAvailabilityDate(entry)
  if (!poster || !availabilityDate) {
    return null
  }

  const isMovie = entry.type === 'MOVIE'
  const fallbackProjectType = (projectType as Media['projectType']) ?? 'series'
  const derivedProjectType: Media['projectType'] = isMovie
    ? 'movie'
    : projectType === 'movie' && entry.projectSlug && entry.projectSlug !== currentSlug
    ? 'series'
    : fallbackProjectType
  const shouldDisableLink = entry.projectSlug === slugs.homesteadSeries

  return {
    guid: entry.guid,
    title: entry.name ?? undefined,
    subtitle: entry.subtitle ?? undefined,
    description: entry.description ?? undefined,
    poster,
    thumbnailPath: poster,
    projectSlug: entry.projectSlug,
    href: `/watch/${entry.projectSlug}/episode/${entry.guid}`,
    episodeNumber: entry.episodeNumber ?? undefined,
    duration: entry.duration ?? undefined,
    projectType: derivedProjectType,
    watchableAvailabilityStatus: null,
    watchableType: entry.type,
    episodeSlug: entry.slug,
    publiclyAvailableDate: availabilityDate,
    prerelease: true,
    disableLink: shouldDisableLink,
  }
}

const getManualUpcomingContent = (
  slug: string,
  projectType: string | undefined,
  existingGuids: Set<string>,
): Media[] => {
  const normalizedSlug = slug.toLowerCase()
  const lookupSlug = UPCOMING_CONTENT_SLUG_ALIASES[normalizedSlug] ?? normalizedSlug
  const entries = WATCH_COLLECTION_UPCOMING_CONTENT[lookupSlug] ?? []
  const now = Date.now()

  return entries
    .filter((entry) => {
      if (existingGuids.has(entry.guid)) {
        return false
      }
      const availabilityDate = getManualAvailabilityDate(entry)
      if (!availabilityDate) {
        return false
      }
      return Date.parse(availabilityDate) > now
    })
    .sort((a, b) => {
      const dateA = Date.parse(getManualAvailabilityDate(a) ?? '0')
      const dateB = Date.parse(getManualAvailabilityDate(b) ?? '0')
      return dateA - dateB
    })
    .map((entry) => mapManualContentToMedia(entry, projectType, normalizedSlug))
    .filter(isDefined)
}

const mapCollectionNodeToMedia = (
  node: WatchCollectionItem | null | undefined,
  options: TransformNodeOptions,
): Media | null => {
  if (!node) return null
  if (isWatchCollectionMovie(node)) {
    return mapWatchCollectionMovieNodeToMedia(node)
  }
  if (isWatchCollectionEpisode(node)) {
    const media = mapWatchCollectionEpisodeNodeToMedia(node, options.slug, options.projectType)
    if (!media) return null
    const shouldFilterSneakPeek = isASneakPeek({ slug: media.episodeSlug ?? '', name: media.title ?? '' })
    if (shouldFilterSneakPeek && (options.isGeoblockedProject || options.isGuildMember)) {
      return null
    }
    return media
  }
  return null
}

const LOADING_PLACEHOLDERS = Array.from({ length: 4 })

export const WatchCollectionTab: React.FC<WatchCollectionTabProps> = ({
  collectionId,
  projectData,
  slug,
  isGeoblockedProject,
  hasWatchableReasons,
}) => {
  const { locale } = useLocale()
  const client = useMemo(() => getWebClient({ locale }), [locale])
  const { guildPermissions, isGuildMember: isGuildMemberUser } = useGuildMember()
  const [medias, setMedias] = useState<Media[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const userHasEarlyAccess = Boolean(guildPermissions.hasEarlyAccess)
  const isSeriesOrPodcast = projectData?.projectType === 'series' || projectData?.projectType === 'podcast'

  useEffect(() => {
    let cancelled = false

    const fetchCollectionEpisodes = async () => {
      setIsLoading(true)

      try {
        const collections = await getWatchCollectionContent([collectionId], client)
        const edges: WatchCollectionEdge[] = collections?.[0]?.connection?.edges?.filter(isDefined) ?? []
        const rawNodes = edges.map((edge) => edge?.node) as Array<WatchCollectionItem | null | undefined>

        const orderedMedias = rawNodes
          .map((node) =>
            mapCollectionNodeToMedia(node, {
              slug,
              projectType: projectData?.projectType,
              isGeoblockedProject,
              isGuildMember: isGuildMemberUser,
            }),
          )
          .filter(isDefined)

        const existingGuids = new Set(orderedMedias.map((media) => media.guid).filter(isDefined))
        const manualUpcomingMedias = getManualUpcomingContent(slug, projectData?.projectType, existingGuids)

        if (!cancelled) {
          setMedias([...orderedMedias, ...manualUpcomingMedias])
        }
      } catch (error) {
        logger().warn('Error fetching watch collection episodes', { collectionId, slug }, error)
        if (!cancelled) {
          setMedias([])
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCollectionEpisodes()

    return () => {
      cancelled = true
    }
  }, [client, collectionId, isGeoblockedProject, isGuildMemberUser, projectData?.projectType, slug])

  return (
    <div className="episodes-tab-container flex h-full w-full flex-col">
      <div className="flex min-h-[80px] flex-col gap-y-10 pl-4 pr-3 md:min-h-[150px] md:gap-y-6 md:px-12 lg:min-h-[280px] lg:gap-y-10 lg:pr-16">
        {isLoading && (
          <div className="flex flex-col gap-y-8">
            {LOADING_PLACEHOLDERS.map((_, index) => (
              <div key={index} className="flex flex-row items-start gap-4">
                <div className="h-[100px] w-full min-w-[180px] animate-pulse rounded-lg bg-core-gray-900/70 md:min-w-[256px] lg:min-w-[300px]" />
                <div className="flex-1 space-y-3">
                  <div className="h-6 w-1/3 animate-pulse rounded bg-core-gray-900/70" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-core-gray-900/70" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-core-gray-900/70" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading &&
          medias.map((media, i) => {
            const resolvedProjectType =
              media.projectType ?? (media.watchableType === 'MOVIE' ? 'movie' : projectData?.projectType)

            return (
              <div key={media?.guid ?? `${media?.projectSlug}-${i}`}>
                <EpisodeCard
                  className="relative flex min-h-[100px] w-full shrink-0 snap-center snap-always flex-row items-start gap-4"
                  thumbnailClassName="h-full w-full min-w-[180px] md:min-w-[256px] lg:min-w-[300px]"
                  {...media}
                  itemIndex={i}
                  pagePlacement={isSeriesOrPodcast ? 0 : 1}
                  imgPriority={i <= 4}
                  includeHoverScale={false}
                  userHasEarlyAccess={userHasEarlyAccess}
                  hasWatchableReasons={hasWatchableReasons}
                  projectType={resolvedProjectType}
                  theaterEpisodeCard
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}

WatchCollectionTab.displayName = 'WatchCollectionTab'
